import React, { useState, useEffect } from 'react';
import { Material } from '../types';
import { apiClient } from '../services/api';

interface MaterialDialogProps {
  isOpen: boolean;
  material: Material | null;
  onSave: (data: { nazev: any; popis: any }) => void;
  onCancel: () => void;
}

const MaterialDialog: React.FC<MaterialDialogProps> = ({
  isOpen,
  material,
  onSave,
  onCancel
}) => {
  const [nazevCz, setNazevCz] = useState('');
  const [nazevEn, setNazevEn] = useState('');
  const [nazevDe, setNazevDe] = useState('');
  const [popisCz, setPopisCz] = useState('');
  const [popisEn, setPopisEn] = useState('');
  const [popisDe, setPopisDe] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMaterialData = async () => {
      if (material) {
        // Pro editaci načteme kompletní data se všemi jazyky
        try {
          setLoading(true);
          setError(null);
          const fullData = await apiClient.getMaterialWithAllLanguages(material.id);
          
          // Předvyplníme všechna pole podle načtených dat
          setNazevCz(fullData.nazev?.cz || '');
          setNazevEn(fullData.nazev?.en || '');
          setNazevDe(fullData.nazev?.de || '');
          setPopisCz(fullData.popis?.cz || '');
          setPopisEn(fullData.popis?.en || '');
          setPopisDe(fullData.popis?.de || '');
        } catch (err: any) {
          setError('Chyba při načítání detailu materiálu: ' + (err.response?.data?.message || err.message));
        } finally {
          setLoading(false);
        }
      } else {
        // Pro nový materiál resetujeme formulář
        setNazevCz('');
        setNazevEn('');
        setNazevDe('');
        setPopisCz('');
        setPopisEn('');
        setPopisDe('');
      }
      setError(null);
    };

    if (isOpen) {
      loadMaterialData();
    }
  }, [material, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validace - název v češtině je povinný
    if (!nazevCz.trim()) {
      setError('Název materiálu v češtině je povinný');
      return;
    }

    const data = {
      nazev: {
        cz: nazevCz.trim(),
        ...(nazevEn.trim() && { en: nazevEn.trim() }),
        ...(nazevDe.trim() && { de: nazevDe.trim() })
      },
      popis: {
        ...(popisCz.trim() && { cz: popisCz.trim() }),
        ...(popisEn.trim() && { en: popisEn.trim() }),
        ...(popisDe.trim() && { de: popisDe.trim() })
      }
    };

    onSave(data);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <h3 style={styles.title}>
          {material ? 'Upravit materiál' : 'Přidat nový materiál'}
        </h3>
        
        {error && <div style={styles.error}>{error}</div>}
        
        {loading ? (
          <div style={styles.loading}>Načítání dat materiálu...</div>
        ) : (
          <form onSubmit={handleSubmit}>
          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Název (povinný v češtině)</h4>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Čeština *</label>
              <input
                type="text"
                style={styles.input}
                value={nazevCz}
                onChange={(e) => setNazevCz(e.target.value)}
                placeholder="Název materiálu v češtině"
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Angličtina</label>
              <input
                type="text"
                style={styles.input}
                value={nazevEn}
                onChange={(e) => setNazevEn(e.target.value)}
                placeholder="Material name in English"
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Němčina</label>
              <input
                type="text"
                style={styles.input}
                value={nazevDe}
                onChange={(e) => setNazevDe(e.target.value)}
                placeholder="Materialname auf Deutsch"
              />
            </div>
          </div>

          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>Popis (volitelný)</h4>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Čeština</label>
              <textarea
                style={styles.textarea}
                value={popisCz}
                onChange={(e) => setPopisCz(e.target.value)}
                placeholder="Popis materiálu v češtině"
                rows={3}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Angličtina</label>
              <textarea
                style={styles.textarea}
                value={popisEn}
                onChange={(e) => setPopisEn(e.target.value)}
                placeholder="Material description in English"
                rows={3}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Němčina</label>
              <textarea
                style={styles.textarea}
                value={popisDe}
                onChange={(e) => setPopisDe(e.target.value)}
                placeholder="Materialbeschreibung auf Deutsch"
                rows={3}
              />
            </div>
          </div>

          <div style={styles.buttons}>
            <button type="button" style={styles.cancelButton} onClick={onCancel}>
              Zrušit
            </button>
            <button type="submit" style={styles.saveButton}>
              {material ? 'Uložit změny' : 'Vytvořit'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  dialog: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  title: {
    margin: '0 0 20px 0',
    fontSize: '24px',
    color: '#333',
  },
  error: {
    padding: '12px',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    marginBottom: '16px',
  },
  loading: {
    padding: '40px',
    textAlign: 'center' as const,
    color: '#666',
    fontSize: '16px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    color: '#555',
    fontWeight: '600' as const,
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    color: '#333',
    fontWeight: '500' as const,
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    color: '#666',
    backgroundColor: '#f8f9fa',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  saveButton: {
    padding: '10px 20px',
    fontSize: '14px',
    color: 'white',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default MaterialDialog;
